import { motion } from "framer-motion";
import { useState } from "react";
import ScrollReveal from "./scroll-reveal";

const showcaseProducts = [
  {
    id: 1,
    name: "Executive Collection",
    images: [
      "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600",
      "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600",
      "https://images.unsplash.com/photo-1549497538-303791108f95?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
    ],
    description: "Premium executive furniture collection featuring ergonomic chairs, modern desks, and luxury storage solutions.",
    features: ["Premium Leather", "Ergonomic Design", "Adjustable Height", "Built-in Storage"],
    price: "₹1,25,000"
  },
  {
    id: 2,
    name: "Modern Workspace",
    images: [
      "https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600",
      "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600",
      "https://images.unsplash.com/photo-1565814329452-e1efa11c5b89?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
    ],
    description: "Contemporary workspace solutions designed for modern professionals and collaborative environments.",
    features: ["Modular Design", "Cable Management", "Sustainable Materials", "Easy Assembly"],
    price: "₹85,000"
  }
];

export default function ProductShowcase() {
  const [activeProduct, setActiveProduct] = useState(0);
  const [activeImage, setActiveImage] = useState(0);

  return (
    <section className="py-20 bg-gradient-to-br from-secondary to-white dark:from-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-playfair font-bold mb-4">
              Featured Collections
            </h2>
            <p className="text-xl text-neutral max-w-2xl mx-auto">
              Discover our most popular furniture collections designed for modern workspaces
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Product Images */}
          <ScrollReveal>
            <div className="relative">
              <motion.div
                key={`${activeProduct}-${activeImage}`}
                className="aspect-square rounded-2xl overflow-hidden shadow-2xl"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src={showcaseProducts[activeProduct].images[activeImage]}
                  alt={showcaseProducts[activeProduct].name}
                  className="w-full h-full object-cover"
                />
              </motion.div>

              {/* Image Navigation Dots */}
              <div className="flex justify-center mt-4 space-x-2">
                {showcaseProducts[activeProduct].images.map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setActiveImage(index)}
                    className={`w-3 h-3 rounded-full transition-all hover-cursor ${
                      index === activeImage ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  />
                ))}
              </div>

              {/* Thumbnail Grid */}
              <div className="grid grid-cols-3 gap-2 mt-4">
                {showcaseProducts[activeProduct].images.map((image, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setActiveImage(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all hover-cursor ${
                      index === activeImage ? 'border-accent' : 'border-transparent'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <img
                      src={image}
                      alt={`${showcaseProducts[activeProduct].name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.button>
                ))}
              </div>
            </div>
          </ScrollReveal>

          {/* Product Details */}
          <ScrollReveal delay={0.3}>
            <div>
              {/* Product Switcher */}
              <div className="flex space-x-4 mb-8">
                {showcaseProducts.map((product, index) => (
                  <motion.button
                    key={product.id}
                    onClick={() => {
                      setActiveProduct(index);
                      setActiveImage(0);
                    }}
                    className={`px-6 py-3 rounded-full font-medium transition-all hover-cursor ${
                      index === activeProduct
                        ? 'bg-accent text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-accent hover:text-white'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {product.name}
                  </motion.button>
                ))}
              </div>

              <motion.div
                key={activeProduct}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-3xl font-playfair font-bold mb-4">
                  {showcaseProducts[activeProduct].name}
                </h3>
                
                <p className="text-lg text-neutral mb-6 leading-relaxed">
                  {showcaseProducts[activeProduct].description}
                </p>

                <div className="mb-6">
                  <h4 className="text-xl font-semibold mb-3">Key Features:</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {showcaseProducts[activeProduct].features.map((feature, index) => (
                      <motion.div
                        key={index}
                        className="flex items-center space-x-2"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="w-2 h-2 bg-accent rounded-full"></div>
                        <span className="text-neutral">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold text-accent">
                    {showcaseProducts[activeProduct].price}
                  </div>
                  <div className="flex space-x-3">
                    <motion.button
                      className="px-6 py-3 bg-primary dark:bg-accent text-white rounded-lg font-semibold hover:bg-opacity-90 transition-all hover-cursor"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Get Quote
                    </motion.button>
                    <motion.button
                      className="px-6 py-3 border-2 border-primary dark:border-accent text-primary dark:text-accent rounded-lg font-semibold hover:bg-primary hover:text-white dark:hover:bg-accent dark:hover:text-white transition-all hover-cursor"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      View Details
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}